import Skeleton from '@mui/material/Skeleton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { makeStyles } from '@mui/styles';
import { useIntl } from 'react-intl';
import { Feature, getProductUnit, Invoice, InvoiceState, PlanInterval } from '../../models';
import { Plan } from '../../models/plan';
import { formatUSPrice } from '../../utils/utils';
import { RecurlySubscriptionDTO } from '../../models/recurlySubscriptionDTO';

const useStyles = makeStyles(theme => ({
  name: {
    fontSize: 14,
    fontWeight: 500,
    color: '#233145',
    margin: theme.spacing(0),
  },
  tableCell: {
    padding: theme.spacing(1.75, 0),
    maxWidth: theme.spacing(33),
    '&.MuiTableCell-root': {
      paddingBottom: theme.spacing(1),
      borderBottom: '1px solid #DCDFE6',
    },
  },
  tableInnerCell: {
    padding: theme.spacing(2, 0),
    maxWidth: theme.spacing(33),
    color: '#172B4D',
  },
  cycleSubscription: {
    fontWeight: 500,
  },
  tableInnerCellPricing: {
    padding: theme.spacing(2, 0),
    maxWidth: theme.spacing(33),
    color: '#172B4D',
    fontWeight: 500,
  },
  statusSuccesful: {
    color: '#008060',
  },
  statusFailed: {
    color: '#e11900',
  },
  statusProcessing: {
    color: '#fa8c16',
  },
}));

interface InvoiceTableProps {
  singleProductInvoices: (Invoice)[];
}

const InvoiceTable = (props: InvoiceTableProps) => {
  const {
    singleProductInvoices,
  } = props;
  const intl = useIntl();
  const classes = useStyles();
  const getPlanIntervalName = (plan: Plan) => {
    switch (plan?.interval) {
      case PlanInterval.MONTH:
        return intl.formatMessage({ id: 'usage_plan_interval_monthly' });
      case PlanInterval.YEAR:
        return intl.formatMessage({ id: 'usage_plan_interval_yearly' });
      default:
        return '';
    }
  };

  const getFeatureName = (feature: Feature | undefined) => (feature
    ? intl.formatMessage({ id: `feature.${feature.toLowerCase()}.product.name` })
    : '');

  const getInvoiceStatusFormatColor = (invoiceStatus: string | undefined) => {
    switch (invoiceStatus) {
      case InvoiceState.PAID:
        return (
          <span className={classes.statusSuccesful}>
            <b>
              {intl.formatMessage({ id: 'invoice_state.paid' })}
            </b>
          </span>
        );
      case InvoiceState.FAILED:
        return (
          <span className={classes.statusFailed}>
            <b>
              {intl.formatMessage({ id: 'invoice_state.failed' })}
            </b>
          </span>
        );
      case InvoiceState.PENDING:
      case InvoiceState.PAST_DUE:
      case InvoiceState.PROCESSING:
        return (
          <span className={classes.statusProcessing}>
            <b>
              {intl.formatMessage({ id: 'invoice_state.processing' })}
            </b>
          </span>
        );
      default:
        return (<b>Unknown</b>);
    }
  };

  const generateSingleProductRow = (invoice: Invoice, subscription: RecurlySubscriptionDTO) => (
    <>
      <TableRow>
        <TableCell className={classes.tableInnerCell}>
          {!subscription ? <Skeleton width="50%" /> : (
            <>
              <p className={classes.name}>
                {getFeatureName(subscription?.plan?.enterpriseProduct?.feature)}
              </p>
              {intl.formatMessage(
                { id: 'payment_success.order_summary.table.product_info' },
                {
                  quantity: formatUSPrice(subscription.quantity),
                  unit: getProductUnit(subscription?.plan?.enterpriseProduct?.feature),
                },
              )}
            </>
          )}
        </TableCell>
        <TableCell className={classes.tableInnerCell}>
          {!subscription ? <Skeleton width="50%" /> : intl.formatMessage(
            { id: 'payment_success.order_summary.table.plan_interval' },
            {
              // eslint-disable-next-line max-len
              planName: <span className={classes.cycleSubscription}>{getPlanIntervalName(subscription?.plan!!)}</span>,
              br: <br />,
              startDate: intl.formatDate(new Date(subscription?.startDate || ''), {
                month: 'short',
                day: '2-digit',
                year: 'numeric',
              }),
              endDate: intl.formatDate(new Date(subscription?.endDate || ''), {
                month: 'short',
                day: '2-digit',
                year: 'numeric',
              }),
            },
          )}
        </TableCell>
        <TableCell className={classes.tableInnerCellPricing}>
          {!invoice
            ? <Skeleton width="50%" />
            : intl.formatNumber(Math.min(Number(subscription?.subTotal), Number(invoice.total)))}
        </TableCell>
        <TableCell className={classes.tableInnerCell}>
          {!subscription ? <Skeleton width="50%" /> : getInvoiceStatusFormatColor(invoice?.state)}
        </TableCell>
      </TableRow>
    </>
  );

  const generateSingleProductStatusRows = () => singleProductInvoices
    .map(invoice => invoice?.subscriptions
      ?.map(subscription => generateSingleProductRow(invoice, subscription)));

  return (
    <TableContainer id="invoice-table">
      <Table>
        <TableHead>
          <TableRow>
            <TableCell className={classes.tableCell}>
              {intl.formatMessage({ id: 'payment_success.order_summary.table.product' })}
            </TableCell>
            <TableCell className={classes.tableCell}>
              {intl.formatMessage({
                id: 'payment_success.order_summary.table.description',
              })}
            </TableCell>
            <TableCell className={classes.tableCell}>
              {intl.formatMessage({ id: 'payment_success.order_summary.table.amount' })}
            </TableCell>
            <TableCell className={classes.tableCell}>
              {intl.formatMessage({ id: 'payment_success.order_summary.table.status' })}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {generateSingleProductStatusRows()}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default InvoiceTable;
